import React from 'react';
import {
  IconButton,
  Button,
  Tooltip
} from '@mui/material';
import {
  Mic as MicIcon,
  FiberManualRecord as RecordIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useRecorder } from '../../contexts/RecorderContext';

/**
 * AudioRecorderButton - Simplified trigger button for audio recording
 *
 * The actual recording UI is now handled by:
 * - FloatingRecorder: Shows during recording with controls
 * - RecorderMetadataModal: Shows after recording for metadata entry
 *
 * This button simply triggers the start of a recording.
 */
const AudioRecorderButton = ({
  entityId,
  entityName,
  _onSave,
  compact = false,
  showInHeader = false,
}) => {
  const {
    isRecording,
    isReady,
    showRecorder,
    toggleExpanded,
  } = useRecorder();

  const handleClick = () => {
    if (isRecording || isReady) {
      // If already recording or in ready state, expand the floating recorder
      toggleExpanded();
    } else {
      // Show recorder in ready state (don't auto-start)
      if (!entityId) {
        toast.error('Please select an entity first');
        return;
      }
      showRecorder(entityId, entityName);
    }
  };

  if (showInHeader) {
    return (
      <Tooltip title={isRecording ? "Recording..." : "Record Audio Note"}>
        <IconButton
          onClick={handleClick}
          sx={{
            color: isRecording ? 'error.main' : 'inherit',
            animation: isRecording ? 'pulse-red 2s infinite' : 'none',
            '@keyframes pulse-red': {
              '0%': {
                boxShadow: '0 0 0 0 rgba(255, 0, 0, 0.7)',
              },
              '70%': {
                boxShadow: '0 0 0 10px rgba(255, 0, 0, 0)',
              },
              '100%': {
                boxShadow: '0 0 0 0 rgba(255, 0, 0, 0)',
              },
            },
          }}
        >
          {isRecording ? <RecordIcon /> : <MicIcon />}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      variant={compact ? "outlined" : "contained"}
      startIcon={isRecording ? <RecordIcon /> : <MicIcon />}
      onClick={handleClick}
      color={isRecording ? "error" : "primary"}
      size={compact ? "small" : "medium"}
      sx={{
        animation: isRecording ? 'pulse-red 2s infinite' : 'none',
        '@keyframes pulse-red': {
          '0%': {
            boxShadow: '0 0 0 0 rgba(255, 0, 0, 0.7)',
          },
          '70%': {
            boxShadow: '0 0 0 10px rgba(255, 0, 0, 0)',
          },
          '100%': {
            boxShadow: '0 0 0 0 rgba(255, 0, 0, 0)',
          },
        },
      }}
    >
      {isRecording ? 'Recording...' : 'Record Audio'}
    </Button>
  );
};

export default AudioRecorderButton;
